import { useEffect, useRef, useState } from 'react';
import { apiGetRaw } from '../api/client';

export function SourceView({ pid, rid }: { pid: string; rid: string }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLPreElement | null>(null);

  // Parse ?span=<start>-<end> out of the hash.
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  const spanMatch = hash.match(/[?&]span=(\d+)-(\d+)/);
  const span = spanMatch
    ? { start: parseInt(spanMatch[1], 10), end: parseInt(spanMatch[2], 10) }
    : null;

  useEffect(() => {
    let cancelled = false;
    void apiGetRaw(
      `/api/wiki/projects/${encodeURIComponent(pid)}/sources/${encodeURIComponent(rid)}/text`,
    )
      .then((r) => r.text())
      .then((t) => {
        if (!cancelled) setText(t);
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [pid, rid]);

  useEffect(() => {
    if (!text || !span || !ref.current) return;
    // Tiny scroll-into-view by counting lines up to span.start.
    const line = (text.slice(0, span.start).match(/\n/g) ?? []).length;
    const lineHeight = 22;
    const top = Math.max(0, line * lineHeight - 80);
    ref.current.scrollTo({ top, behavior: 'instant' });
  }, [text, span]);

  if (error)
    return (
      <div style={{ padding: 24 }}>
        <a href={`#/wiki/p/${encodeURIComponent(pid)}`}>← Project</a>
        <div className="banner banner-warning">{error}</div>
      </div>
    );
  if (text === null) return <div style={{ padding: 24 }}>Loading…</div>;

  // Render the source plaintext, highlighting the span if present.
  let before = text;
  let span_text = '';
  let after = '';
  if (span) {
    before = text.slice(0, span.start);
    span_text = text.slice(span.start, span.end);
    after = text.slice(span.end);
  }

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <a href={`#/wiki/p/${encodeURIComponent(pid)}`}>← Project</a>
      <h2 style={{ marginTop: 8 }}>Source · {rid}</h2>
      <pre
        ref={ref}
        style={{
          maxHeight: '70vh',
          overflow: 'auto',
          background: 'var(--bg-elev, #181818)',
          padding: 12,
          borderRadius: 6,
          whiteSpace: 'pre-wrap',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        {span ? (
          <>
            {before}
            <mark
              style={{
                background: 'rgba(125,211,252,0.3)',
                padding: '0 2px',
                borderRadius: 2,
              }}
            >
              {span_text}
            </mark>
            {after}
          </>
        ) : (
          text
        )}
      </pre>
    </div>
  );
}
